/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import dayjs from "dayjs";
import {
  ALL_FILTER_VALUE,
  PERQ_REPORT_DATE_FILTERS,
  resolveDateRange,
} from "../../../utils/perqReportDrilldown";
import { isValidDayjsValue } from "../../../utils/isValidDayjsValue";
import { isValidCustomDateRange } from "../../../utils/isValidCustomDateRange";

// Shared with the Perq Report so the presets stay in step with resolveDateRange's cases.
export const DATE_FILTERS = PERQ_REPORT_DATE_FILTERS;

// "all" is the unselected state (no has_ot_discount sent), so it isn't offered as an option.
export const ALL_ORDER_TYPE = "all";
export const ORDER_TYPES = ["ot", "non_ot"];
export const ORDER_TYPE_PLACEHOLDER = "Orders";

export const ORDER_TYPE_LABELS = {
  ot: "OwnersTable",
  non_ot: "Non-OwnersTable",
};

export const DEFAULT_SORT_BY = "restaurant_name";
export const DEFAULT_SORT_ORDER = "asc";

export const defaultCheckInComplianceFilters = () => ({
  dateFilter: "Last 7 Days",
  customDateFrom: dayjs(),
  customDateTo: dayjs(),
  restaurant_group_id: ALL_FILTER_VALUE,
  restaurant_ids: [ALL_FILTER_VALUE],
  order_type: ALL_ORDER_TYPE,
  applied_discount_names: [],
});

export const SELECT_GROUP_ERROR = "Select a restaurant group";
export const MISSING_DATE_RANGE_ERROR = "Select both from and through dates";
export const INVALID_DATE_RANGE_ERROR = "From date cannot be after through date";

// @returns {string|null} why Update is refused, mirroring the backend's required group and date_range
export const getCheckInComplianceFilterError = (filters) => {
  if (!filters?.restaurant_group_id || filters.restaurant_group_id === ALL_FILTER_VALUE) {
    return SELECT_GROUP_ERROR;
  }
  if (filters.dateFilter !== "Custom Date") return null;

  if (!isValidDayjsValue(filters.customDateFrom) || !isValidDayjsValue(filters.customDateTo)) {
    return MISSING_DATE_RANGE_ERROR;
  }
  if (!isValidCustomDateRange(filters.customDateFrom, filters.customDateTo)) {
    return INVALID_DATE_RANGE_ERROR;
  }
  return null;
};

// Backend requires restaurant_group_id and date_range; callers gate the request on both.
export const getCheckInComplianceApiFilters = (filters) => {
  const params = {};

  const dateRange = resolveDateRange(filters);
  if (dateRange) params.date_range = dateRange;

  if (filters?.restaurant_group_id && filters.restaurant_group_id !== ALL_FILTER_VALUE) {
    params.restaurant_group_id = filters.restaurant_group_id;
  }

  if (Array.isArray(filters?.restaurant_ids) && !filters.restaurant_ids.includes(ALL_FILTER_VALUE)) {
    params.restaurant_ids = filters.restaurant_ids;
  }

  if (filters?.order_type === "ot") params.has_ot_discount = true;
  if (filters?.order_type === "non_ot") params.has_ot_discount = false;

  if (filters?.applied_discount_names?.length) {
    params.applied_discount_names = filters.applied_discount_names;
  }

  return params;
};
